import { BillingAddress } from '@models/billing-address';
import { ContractConditions } from '@models/contract-conditions';
import { Customer } from '@models/customer';
import { InsuranceType, insuranceTypeToDto } from '@models/insurance-type';
import { Insurant, InsurantDto, insurantToDto } from '@models/insurant';
import { InsuredObject, insuredObjectToDto } from '@models/insured-object';
import { PaymentData } from '@models/payment-data';
import { Product } from '@models/product';
import { ContractRequestDto } from './contract-request';
import { findViableContractStartDate } from 'app/utils/helper.functions';
import { getResellerData } from 'app/utils/reseller-data';
import { Tag } from '@models/tag';
import { ProductAdvice, productAdviceToDto } from '@models/product-advice';

export interface OrderState {
    currentStep: string;
    completedSteps: string[];
    invalidSteps: string[];
    productSlug?: string;
    advice?: ProductAdvice;
    billingAddress?: Partial<BillingAddress>;
    contract?: Partial<ContractConditions>;
    payment?: Partial<PaymentData>;
    insurants: Insurant[];
    insuredObjects: InsuredObject[];
    insurantsFamilyRelationship?: boolean;
    tags: Map<Tag, boolean>;
}

export const isOrderState = (x: unknown): x is OrderState =>
    typeof x === 'object' &&
    x !== null &&
    'currentStep' in x &&
    typeof x.currentStep === 'string' &&
    'completedSteps' in x &&
    Array.isArray(x.completedSteps) &&
    'invalidSteps' in x &&
    Array.isArray(x.invalidSteps) &&
    'tags' in x &&
    x.tags instanceof Map;

export function orderStateToContractRequestDto(
    state: OrderState,
    product: Product,
    insuranceType: InsuranceType,
    customer?: Customer,
): ContractRequestDto {
    const contractRequestDto: ContractRequestDto = {
        // => BillingAddress
        Title:
            state.billingAddress && state.billingAddress.title
                ? { Id: state.billingAddress.title.id, Description: state.billingAddress.title.description }
                : null,
        Company: state.billingAddress?.company || null,
        Firstname: state.billingAddress?.firstName || '',
        Lastname: state.billingAddress?.lastName || '',
        Street: state.billingAddress?.street || '',
        ZIP: state.billingAddress?.zip || '',
        City: state.billingAddress?.city || '',
        Country: { Country: 'Deutschland', Id: 1 }, // TODO: what about this?
        Phone: state.billingAddress?.phone || '',
        Email: state.billingAddress?.mail || '',
        DateOfBirth: state.billingAddress?.dateOfBirth || '',

        // => PaymentData
        AccountHolder: state.payment?.accountHolder || '',
        IBAN: state.payment?.iban || '',
        Bic: state.payment?.bic || null,
        // TODO: ask sinovo to remove this:
        AccountNo: '',
        Bankcode: '',
        NameOfBank: state.payment?.nameOfBank || '',

        // contract data
        InsuranceType: insuranceTypeToDto(insuranceType),
        ContractStart:
            state.contract?.contractStart || findViableContractStartDate(product.orderConfig.contractStartMode),
        ContractPeriod: state.contract?.contractPeriod || 0,
        LegalHealthInsurance: '',
        Newsletter: false, // TODO: add newsletter option or ask sinovo to remove this
        DiscountCode: state.contract?.discountCode || '',

        // TODO: can we scrap that and make it a pool object? dunno
        ...getInstrumentPremiumData(state, insuranceType),

        // insurants:
        Insurants: finalizeInsurants(state.insurants, state.billingAddress),
        InsurantsFamilyRelationship: !!state.insurantsFamilyRelationship,

        // object pool:
        ObjectPool: (state.insuredObjects || []).map((insuredObject) => insuredObjectToDto(insuredObject)),

        // reseller data:
        ResellerUrl: getResellerData()?.resellerId ?? '',
        AdditionalResellerData: getResellerData()?.additionalResellerData ?? '',

        // product advice:
        ...(state.advice && { Advice: productAdviceToDto(state.advice) }),
    };
    if (typeof customer?.id === 'number') {
        contractRequestDto.CustomerId = customer.id;
    }
    return contractRequestDto;
}

function getInstrumentPremiumData(
    state: OrderState,
    insuranceType: InsuranceType,
): { InsuranceObjectId: number | null; ObjectValue: number | null } {
    if (
        insuranceType.name === 'Musik' &&
        state.insuredObjects &&
        state.insuredObjects[0] &&
        typeof state.insuredObjects[0].description === 'number' &&
        typeof state.insuredObjects[0].value === 'number'
    ) {
        return {
            InsuranceObjectId: state.insuredObjects[0].description,
            ObjectValue: state.insuredObjects[0].value,
        };
    }
    return {
        InsuranceObjectId: null,
        ObjectValue: null,
    };
}

function insurantFromBillingAddress(
    billingAddress: Partial<BillingAddress> = {
        firstName: '',
        lastName: '',
        dateOfBirth: '',
    },
): Insurant {
    return {
        firstName: billingAddress.firstName || '',
        lastName: billingAddress.lastName || '',
        dateOfBirth: billingAddress.dateOfBirth || '',
        isInsuree: true,
        isChild: false,
    };
}

export function finalizeInsurants(insurants: Insurant[], billingAddress: Partial<BillingAddress>): InsurantDto[] {
    if (insurants.length === 0) {
        return [insurantToDto(insurantFromBillingAddress(billingAddress))];
    } else {
        return insurants.map((insurant) =>
            insurantToDto(
                insurant.isInsuree
                    ? ({
                          ...insurant,
                          firstName: insurant.firstName || (billingAddress?.firstName ?? ''),
                          lastName: insurant.lastName || (billingAddress?.lastName ?? ''),
                          dateOfBirth: insurant.dateOfBirth || (billingAddress?.dateOfBirth ?? ''),
                      } as Insurant)
                    : insurant,
            ),
        );
    }
}
